import React, { FunctionComponent, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';

import {
  AddButton,
  CardList,
  SaveEmailSubmitButtonGroup,
  ScreenReaderUpdateText,
} from 'components';
import { Heading, Text } from 'components/chroma';

import { RefDataType, YesNoAnswer } from 'scenes/common/types';
import { getApiData } from 'scenes/common/helpers/apiData';

import { HomeFullPageProps } from 'scenes/home/types';
import {
  getSpecifiedContentsHomeOnly,
  getSpecifiedPortableContentsAustraliaWide,
} from 'scenes/home/selectors';

import { ItemDetailsCard } from './ItemDetailsCard';

const ButtonWrapper = styled.div`
  margin-top: 1.5rem;
`;

export const SpecifiedContentList: FunctionComponent<Pick<
  HomeFullPageProps,
  | 'funnelState'
  | 'referenceData'
  | 'onNext'
  | 'onRemoveSpecifiedContent'
  | 'onClickSaveQuoteModal'
>> = ({
  funnelState,
  referenceData,
  onRemoveSpecifiedContent,
  onNext,
  onClickSaveQuoteModal,
}) => {
  const {
    formState: { isValid },
    handleSubmit,
  } = useForm({
    mode: 'onChange',
    criteriaMode: 'all',
  });

  const specifiedContentsHomeOnly = getSpecifiedContentsHomeOnly(funnelState);
  const specifiedPortableContentsAustraliaWide = getSpecifiedPortableContentsAustraliaWide(
    funnelState
  );

  const { specifiedPortableCategory } = getApiData(referenceData);

  const onSubmit = useCallback(() => {
    onNext({ specifiedContentsAnswer: YesNoAnswer.No });
  }, [onNext]);

  const handleAddAdditionalItem = useCallback(() => {
    onNext({ specifiedContentsAnswer: YesNoAnswer.Yes });
  }, [onNext]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <ScreenReaderUpdateText />
      <Heading size={3}>
        {[
          ...specifiedContentsHomeOnly,
          ...specifiedPortableContentsAustraliaWide,
        ].length > 0
          ? 'You added these listed items to your cover'
          : 'No items added'}
      </Heading>
      <Heading
        size={4}
        margin={{ bottom: specifiedContentsHomeOnly.length < 1 ? 0 : 4 }}
      >
        Specified contents in the home
      </Heading>
      {specifiedContentsHomeOnly.length > 0 ? (
        <CardList marginBottom="1rem" role="list">
          {specifiedContentsHomeOnly.map((item) => (
            <ItemDetailsCard
              itemCategoryTitle={
                specifiedPortableCategory.find(
                  (category: RefDataType) => category.value === item.category
                )?.label
              }
              key={`${item.description}${item.itemValue}`}
              item={item}
              onClickEdit={onNext}
              onClickRemove={onRemoveSpecifiedContent}
            />
          ))}
        </CardList>
      ) : (
        <Text>No items</Text>
      )}

      <Heading
        size={4}
        margin={{
          bottom: specifiedPortableContentsAustraliaWide.length < 1 ? 0 : 4,
        }}
      >
        Portable specified contents Australia-wide
      </Heading>
      {specifiedPortableContentsAustraliaWide.length > 0 ? (
        <CardList marginBottom="1rem" role="list">
          {specifiedPortableContentsAustraliaWide.map((item) => (
            <ItemDetailsCard
              itemCategoryTitle={
                specifiedPortableCategory.find(
                  (category: RefDataType) => category.value === item.category
                )?.label
              }
              key={`${item.description}${item.itemValue}`}
              item={item}
              onClickEdit={onNext}
              onClickRemove={onRemoveSpecifiedContent}
            />
          ))}
        </CardList>
      ) : (
        <Text>No items</Text>
      )}
      <AddButton onClick={handleAddAdditionalItem}>Add another item</AddButton>
      <ButtonWrapper>
        <SaveEmailSubmitButtonGroup
          onClickSaveQuoteModal={onClickSaveQuoteModal}
          submitEnabled={isValid}
        />
      </ButtonWrapper>
    </form>
  );
};

export default SpecifiedContentList;
